import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { HelpButton } from '@/components/HelpButton';
import { useOwnership } from '@/components/OwnerBadge';
import { EmptyState } from '@/components/EmptyState';
import { HelpText, PageHint } from '@/components/HelpText';
import { Modal } from '@/components/Modal';
import { useToast } from '@/components/Toast';
import {
  ALL_PAYMENT_METHODS,
  PAYMENT_METHOD_LABEL,
  fmtBRL,
  fmtDate,
  listAccounts,
  listCategories,
  type PaymentMethod,
} from '@/lib/finance';
import {
  FREQUENCY_LABEL,
  WEEKDAY_LABEL,
  createRecurrence,
  deleteRecurrence,
  describeSchedule,
  listRecurrences,
  runRecurrenceNow,
  updateRecurrence,
  type Frequency,
  type Recurrence,
} from '@/lib/recurrences';

export function RecurrencesPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const recsQ = useQuery({ queryKey: ['recurrences'], queryFn: listRecurrences });
  const accountsQ = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const { myId, firstName } = useOwnership();
  const [open, setOpen] = useState(false);

  const accountName = useMemo(() => {
    const map = new Map(
      accountsQ.data?.map((a) => [
        a.id,
        a.owner_user_id !== myId ? `${a.name} · ${firstName(a.owner_user_id)}` : a.name,
      ]),
    );
    return (id: string) => map.get(id) ?? '—';
  }, [accountsQ.data, myId, firstName]);

  function invalidateAll() {
    qc.invalidateQueries({ queryKey: ['recurrences'] });
    qc.invalidateQueries({ queryKey: ['transactions'] });
    qc.invalidateQueries({ queryKey: ['accounts'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  }

  const toggleMutation = useMutation({
    mutationFn: (r: Recurrence) => updateRecurrence(r.id, { active: !r.active }),
    onSuccess: (r) => {
      toast.success(r.active ? 'recorrência reativada' : 'recorrência pausada');
      qc.invalidateQueries({ queryKey: ['recurrences'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const runMutation = useMutation({
    mutationFn: (r: Recurrence) => runRecurrenceNow(r.id),
    onSuccess: (tx) => {
      toast.success(tx ? 'lançamento gerado' : 'já havia lançamento nesse período');
      invalidateAll();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (r: Recurrence) => deleteRecurrence(r.id),
    onSuccess: () => {
      toast.success('recorrência excluída');
      qc.invalidateQueries({ queryKey: ['recurrences'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold">Recorrências</h1>
          <HelpButton topic="recorrencias" />
        </div>
        <button
          onClick={() => setOpen(true)}
          className="tap rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white"
        >
          + nova
        </button>
      </header>

      <PageHint>
        Assinaturas e contas fixas (aluguel, Netflix, salário…) são lançadas{' '}
        <strong>automaticamente</strong> na data programada. Com "lançar sozinho" desligado, o
        lançamento é criado como <em>pendente</em> pra você confirmar.
      </PageHint>

      {recsQ.isLoading && <p className="text-slate-500">carregando…</p>}

      {recsQ.data?.length === 0 && (
        <EmptyState
          title="Nenhuma recorrência ainda"
          hint="Cadastre suas assinaturas e contas fixas pra nunca mais esquecer um lançamento."
          icon="🔁"
          action={
            <button
              onClick={() => setOpen(true)}
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white"
            >
              Criar primeira recorrência
            </button>
          }
        />
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {recsQ.data?.map((r) => (
          <div
            key={r.id}
            className={`flex flex-col gap-3 rounded-xl border bg-white p-4 shadow-sm ${
              r.active ? 'border-slate-200' : 'border-slate-200 opacity-60'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-medium">{r.description}</p>
                <p className="truncate text-xs text-slate-500">
                  {r.vendor ? `${r.vendor} · ` : ''}
                  {accountName(r.account_id)}
                  {r.payment_method ? ` · ${PAYMENT_METHOD_LABEL[r.payment_method]}` : ''}
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap justify-end gap-1">
                {r.is_subscription && (
                  <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] text-violet-700">
                    assinatura
                  </span>
                )}
                {r.auto_post && (
                  <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-700">
                    automática
                  </span>
                )}
                {!r.active && (
                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700">
                    pausada
                  </span>
                )}
              </div>
            </div>

            <div>
              <p
                className={`text-lg font-semibold ${
                  r.direction === 'out' ? 'text-red-600' : 'text-emerald-600'
                }`}
              >
                {r.direction === 'out' ? '−' : '+'}
                {fmtBRL(r.amount)}
              </p>
              <p className="text-xs text-slate-500">
                {FREQUENCY_LABEL[r.frequency]} · {describeSchedule(r)}
              </p>
              <p className="text-xs text-slate-500">
                {r.active ? `próxima em ${fmtDate(r.next_run_date)}` : 'sem próximas execuções'}
                {r.end_date && ` · até ${fmtDate(r.end_date)}`}
              </p>
            </div>

            <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-2 text-xs">
              <button
                onClick={() => runMutation.mutate(r)}
                disabled={runMutation.isPending || !r.active}
                className="tap font-medium text-slate-900 hover:underline disabled:text-slate-300"
              >
                lançar agora
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => toggleMutation.mutate(r)}
                  disabled={toggleMutation.isPending}
                  className="tap text-slate-500 hover:underline"
                >
                  {r.active ? 'pausar' : 'reativar'}
                </button>
                <button
                  onClick={() => {
                    if (window.confirm(`Excluir a recorrência "${r.description}"?`)) {
                      deleteMutation.mutate(r);
                    }
                  }}
                  disabled={deleteMutation.isPending}
                  className="tap text-red-600 hover:underline"
                >
                  excluir
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <CreateRecurrenceModal
        open={open}
        onClose={() => setOpen(false)}
        onCreated={() => {
          setOpen(false);
          toast.success('recorrência criada');
          qc.invalidateQueries({ queryKey: ['recurrences'] });
        }}
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-slate-700">{label}</span>
      {children}
    </label>
  );
}

function CreateRecurrenceModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const accountsQ = useQuery({ queryKey: ['accounts'], queryFn: listAccounts, enabled: open });
  const categoriesQ = useQuery({
    queryKey: ['categories'],
    queryFn: listCategories,
    enabled: open,
  });
  const accounts = useMemo(() => accountsQ.data ?? [], [accountsQ.data]);
  const { myId, firstName } = useOwnership();

  const [direction, setDirection] = useState<'in' | 'out'>('out');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod | ''>('');
  const [accountId, setAccountId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [frequency, setFrequency] = useState<Frequency>('monthly');
  const [interval, setInterval] = useState('1');
  const [dayOfMonth, setDayOfMonth] = useState('');
  const [weekday, setWeekday] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState('');
  const [isSubscription, setIsSubscription] = useState(false);
  const [vendor, setVendor] = useState('');
  const [autoPost, setAutoPost] = useState(false);
  const [isShared, setIsShared] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // contas que aceitam o método escolhido (sem método: todas)
  const eligibleAccounts = useMemo(() => {
    if (!method) return accounts;
    return accounts.filter((a) => a.supported_methods.includes(method));
  }, [method, accounts]);

  useEffect(() => {
    if (eligibleAccounts.length === 1) {
      setAccountId(eligibleAccounts[0].id);
    } else if (accountId && !eligibleAccounts.find((a) => a.id === accountId)) {
      setAccountId('');
    }
  }, [eligibleAccounts, accountId]);

  const filteredCategories = (categoriesQ.data ?? []).filter(
    (c) => c.kind === (direction === 'out' ? 'expense' : 'income') || c.kind === 'both',
  );

  const mutation = useMutation({
    mutationFn: createRecurrence,
    onSuccess: () => {
      setDescription('');
      setAmount('');
      setMethod('');
      setAccountId('');
      setCategoryId('');
      setFrequency('monthly');
      setInterval('1');
      setDayOfMonth('');
      setWeekday('');
      setEndDate('');
      setIsSubscription(false);
      setVendor('');
      setAutoPost(false);
      setIsShared(false);
      onCreated();
    },
    onError: (e: Error) => setError(e.message),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!accountId) {
      setError('Escolha a conta.');
      return;
    }
    mutation.mutate({
      description,
      amount,
      direction,
      account_id: accountId,
      category_id: categoryId || null,
      payment_method: method || null,
      is_shared: isShared,
      frequency,
      interval: parseInt(interval, 10) || 1,
      day_of_month: frequency === 'monthly' && dayOfMonth ? parseInt(dayOfMonth, 10) : null,
      weekday: frequency === 'weekly' && weekday !== '' ? parseInt(weekday, 10) : null,
      start_date: startDate,
      end_date: endDate || null,
      is_subscription: isSubscription,
      vendor: vendor || null,
      auto_post: autoPost,
    });
  }

  return (
    <Modal open={open} title="Nova recorrência" onClose={onClose} size="lg">
      <form onSubmit={submit} className="flex flex-col gap-4 text-sm">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setDirection('out')}
            className={`tap flex-1 rounded-md border px-3 py-3 font-medium ${
              direction === 'out'
                ? 'border-red-500 bg-red-50 text-red-700'
                : 'border-slate-300 text-slate-600'
            }`}
          >
            despesa
          </button>
          <button
            type="button"
            onClick={() => setDirection('in')}
            className={`tap flex-1 rounded-md border px-3 py-3 font-medium ${
              direction === 'in'
                ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                : 'border-slate-300 text-slate-600'
            }`}
          >
            receita
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Descrição">
            <input
              required
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder='Ex.: "Netflix", "Aluguel", "Salário"'
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            />
          </Field>
          <Field label="Valor (R$)">
            <input
              required
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            />
          </Field>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-slate-700">
            {direction === 'out' ? '💳 Como paga? (opcional)' : '💳 Como recebe? (opcional)'}
          </span>
          <div className="flex flex-wrap gap-2">
            {ALL_PAYMENT_METHODS.map((m) => {
              const active = method === m;
              const anyAccount = accounts.some((a) => a.supported_methods.includes(m));
              return (
                <button
                  key={m}
                  type="button"
                  disabled={!anyAccount}
                  onClick={() => setMethod(active ? '' : m)}
                  className={`tap rounded-full border px-3 py-1.5 text-sm ${
                    active
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : anyAccount
                        ? 'border-slate-300 text-slate-600'
                        : 'cursor-not-allowed border-slate-200 text-slate-300'
                  }`}
                >
                  {PAYMENT_METHOD_LABEL[m]}
                </button>
              );
            })}
          </div>
          {method === 'credit_card' && (
            <HelpText>A conta será o cartão — cada lançamento vai pra fatura do mês.</HelpText>
          )}
        </div>

        <Field label={direction === 'out' ? '💰 De qual conta sai?' : '💰 Em qual conta entra?'}>
          <select
            required
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
          >
            <option value="">— selecionar —</option>
            {eligibleAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
                {a.owner_user_id !== myId ? ` · ${firstName(a.owner_user_id)}` : ''}
              </option>
            ))}
          </select>
          {method && eligibleAccounts.length === 0 && (
            <HelpText>Nenhuma conta aceita esse método — escolha outro ou edite a conta.</HelpText>
          )}
        </Field>

        <Field label="Categoria (opcional)">
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
          >
            <option value="">— sem categoria —</option>
            {filteredCategories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Frequência">
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as Frequency)}
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            >
              {(Object.keys(FREQUENCY_LABEL) as Frequency[]).map((f) => (
                <option key={f} value={f}>
                  {FREQUENCY_LABEL[f]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="A cada">
            <input
              type="number"
              inputMode="numeric"
              min="1"
              max="99"
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            />
          </Field>
        </div>

        {frequency === 'monthly' && (
          <Field label="Dia do mês (opcional)">
            <input
              type="number"
              inputMode="numeric"
              min="1"
              max="31"
              value={dayOfMonth}
              onChange={(e) => setDayOfMonth(e.target.value)}
              placeholder="dia da data de início"
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            />
            <HelpText>Dia 31 em mês curto cai no último dia do mês.</HelpText>
          </Field>
        )}

        {frequency === 'weekly' && (
          <Field label="Dia da semana (opcional)">
            <select
              value={weekday}
              onChange={(e) => setWeekday(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            >
              <option value="">mesmo dia da data de início</option>
              {WEEKDAY_LABEL.map((w, i) => (
                <option key={w} value={i}>
                  {w}
                </option>
              ))}
            </select>
          </Field>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Começa em">
            <input
              required
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            />
          </Field>
          <Field label="Termina em (opcional)">
            <input
              type="date"
              value={endDate}
              min={startDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            />
          </Field>
        </div>

        <label className="tap flex items-center gap-2">
          <input
            type="checkbox"
            checked={isSubscription}
            onChange={(e) => setIsSubscription(e.target.checked)}
            className="h-5 w-5"
          />
          <span className="text-slate-700">é uma assinatura (Netflix, Spotify…)</span>
        </label>

        {isSubscription && (
          <Field label="Serviço/fornecedor (opcional)">
            <input
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              placeholder="Ex.: Netflix"
              maxLength={120}
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            />
          </Field>
        )}

        <label className="tap flex items-center gap-2">
          <input
            type="checkbox"
            checked={autoPost}
            onChange={(e) => setAutoPost(e.target.checked)}
            className="h-5 w-5"
          />
          <span className="text-slate-700">lançar sozinho (sem pedir confirmação)</span>
        </label>
        <HelpText>
          Desligado: o lançamento é criado como <em>pendente</em> na data programada e você confirma
          em "Transações". Ligado: já entra como efetivado.
        </HelpText>

        <label className="tap flex items-center gap-2">
          <input
            type="checkbox"
            checked={isShared}
            onChange={(e) => setIsShared(e.target.checked)}
            className="h-5 w-5"
          />
          <span className="text-slate-700">compartilhada com household</span>
        </label>

        {error && <p className="text-red-600">{error}</p>}

        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="tap rounded-md border border-slate-300 px-4 py-2 sm:order-1"
          >
            cancelar
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="tap rounded-md bg-slate-900 px-4 py-2 font-medium text-white disabled:opacity-50 sm:order-2"
          >
            {mutation.isPending ? 'criando…' : 'criar recorrência'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
